import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { EmployeeCalendarRegisterStatus } from 'src/app/enums/employee-calendar-register-status.enum';
import { SprintPlanningExecutionMatchStatus } from 'src/app/enums/sprint-planning-execution-match-status.enum';
import { ProductionKeyPerformanceIndicatorEvaluation } from 'src/app/models/production-key-performance-indicator-evaluation.model';
import { SprintPlanningExecutionData, SprintPlanningExecutionItem } from 'src/app/models/sprint-planning-execution-kpi-data.model';

interface SprintPlanningRow {
  sprint_review_date: string;
  sprint_planning_deadline: string;
  sprint_planning_date: string | null;
  match_status: SprintPlanningExecutionMatchStatus;
  planning_status: EmployeeCalendarRegisterStatus;
}

@Component({
  selector: 'app-sprint-planning-execution-details',
  imports: [CommonModule, DatePipe, MatIconModule],
  templateUrl: './sprint-planning-execution-details.component.html',
  styleUrl: './sprint-planning-execution-details.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SprintPlanningExecutionDetailsComponent {
  currentMonthEvaluation = input<ProductionKeyPerformanceIndicatorEvaluation | null>(null);
  protected readonly SprintPlanningExecutionMatchStatus = SprintPlanningExecutionMatchStatus;
  protected readonly EmployeeCalendarRegisterStatus = EmployeeCalendarRegisterStatus;

  detailsData = computed((): SprintPlanningExecutionData | null => {
    const rawData = this.currentMonthEvaluation()?.data;
    if (SprintPlanningExecutionDetailsComponent.isSprintPlanningExecutionData(rawData)) {
      return rawData;
    }

    if (typeof rawData !== 'object' || rawData === null) {
      return null;
    }

    const nestedData = (rawData as { company_product_general_kpi?: { kpi_data?: unknown } }).company_product_general_kpi?.kpi_data;
    return SprintPlanningExecutionDetailsComponent.isSprintPlanningExecutionData(nestedData) ? nestedData : null;
  });

  matchedCount = computed((): number => this.detailsData()?.matched_count ?? 0);
  unmatchedCount = computed((): number => this.detailsData()?.unmatched_count ?? 0);
  upcomingPlanningsCount = computed((): number => this.detailsData()?.upcoming_plannings_count ?? 0);
  evaluatedCount = computed((): number => this.matchedCount() + this.unmatchedCount());

  sprintPlannings = computed((): SprintPlanningRow[] => {
    const data = this.detailsData();
    if (!data) {
      return [];
    }

    return data.sprint_planning_executions_data
      .map(
        (item: SprintPlanningExecutionItem): SprintPlanningRow => ({
          sprint_review_date: item.sprint_review_date,
          sprint_planning_deadline: item.sprint_planning_deadline,
          sprint_planning_date: item.sprint_planning_date,
          match_status: item.match_status,
          planning_status: item.planning_status,
        })
      )
      .sort((a, b) => a.sprint_review_date.localeCompare(b.sprint_review_date));
  });

  public static isSprintPlanningExecutionData(data: unknown): data is SprintPlanningExecutionData {
    if (typeof data !== 'object' || data === null) {
      return false;
    }

    const candidate = data as Partial<SprintPlanningExecutionData>;

    return (
      typeof candidate.matched_count === 'number' &&
      typeof candidate.unmatched_count === 'number' &&
      typeof candidate.upcoming_plannings_count === 'number' &&
      Array.isArray(candidate.sprint_planning_executions_data)
    );
  }
}
